// frontend/src/components/athleteEditor/StaffProfileSections.jsx
// Main-column cards of the staff/partner read view of /athletes/:userId that
// have no older component to sit beside: Academics, Camps attended and the new
// Account activity card. `.adm` look (AdminShell.css, "athlete profile").
import React from 'react';
import { AndroidIcon, AppleIcon, GlobeIcon, VerifiedSeal } from './staffProfileIcons';
import { fmtAgo, fmtDate, fmtDateTime, fmtLastSeen, fmtMonthYear } from '../../lib/staffProfileFormat';

const hasValue = v => v !== null && v !== undefined && v !== '';
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const ACADEMICS = [
  ['gpa_weighted', 'GPA (weighted)', v => Number(v).toFixed(2)],
  ['gpa_unweighted', 'GPA (unweighted)', v => Number(v).toFixed(2)],
  ['act_score', 'ACT', v => `${v}`],
  ['sat_score', 'SAT', v => `${v}`],
];

// All four always render ("—" when missing), so staff can tell missing from
// not applicable -- same rule as the page's AcademicsSection.
export function StaffAcademicsCard({ profile }) {
  const p = profile || {};
  return (
    <section className="adm__card adm__card--flush">
      <div className="adm-card__head"><h2 className="adm-card__title">Academics</h2></div>
      <div className="adm-statgrid">
        {ACADEMICS.map(([field, label, fmt]) => {
          const has = hasValue(p[field]);
          return (
            <div className="adm-stat" key={field}>
              <div className="adm-stat__label">{label}</div>
              <div className={`adm-stat__value${has ? '' : ' adm-stat__value--empty'}`}>
                {has ? fmt(p[field]) : '—'}
                {has && p[`${field}_verified`] && <VerifiedSeal small />}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function StaffCampsCard({ profile }) {
  const camps = (profile && profile.camps) || [];
  return (
    <section className="adm__card">
      <div className="adm-card__head">
        <h2 className="adm-card__title">Camps attended</h2>
        {camps.length > 0 && <span className="adm-card__meta">{camps.length}</span>}
      </div>
      {camps.length === 0 ? (
        <div className="adm-photos__empty">None on record</div>
      ) : (
        <div className="adm-camps">
          {camps.map(c => (
            <div className="adm-camps__row" key={c.id}>
              <span className="adm-camps__name">{c.name}</span>
              <span className="adm-camps__when">{[fmtMonthYear(c.date), c.location].filter(Boolean).join(' · ') || '—'}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const PLATFORM_NAME = { ios: 'iOS', android: 'Android', web: 'Web' };
const CHANNEL = { ios: 'iOS app', android: 'Android app', web: 'web' };
const PROVIDER = { apple: 'Apple', google: 'Google', password: 'Password' };

function PlatformMark({ platform }) {
  const Icon = platform === 'ios' ? AppleIcon : platform === 'android' ? AndroidIcon : GlobeIcon;
  return <><Icon />{PLATFORM_NAME[platform] || platform}</>;
}

function appSub(a) {
  const p = a.primary_platform;
  if (!p) return 'No app or web activity recorded';
  const native = [a.app_version && `v${a.app_version}`, a.last_seen_at && `last seen ${fmtLastSeen(a.last_seen_at)}`]
    .filter(Boolean).join(' · ');
  if (p !== 'web') return native || 'App';
  // Mostly web. Say so, and still name the app if they have used one.
  if (a.app_platform) return `Mostly browser · ${CHANNEL[a.app_platform] || a.app_platform}${native ? ` ${native}` : ''}`;
  return `Browser only · no app seen in ${a.window_days || 90} days`;
}

function platformsSeen(share) {
  const entries = Object.entries(share || {}).sort((x, y) => y[1] - x[1]);
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => `${PLATFORM_NAME[k] || k} ${Math.round(v * 100)}%`).join(' · ');
}

/**
 * Signup, logins and the app they use. Rendered only when the profile payload
 * carries `activity`, which the server sends to staff and partners alone.
 */
export function AccountActivityCard({ user, activity }) {
  const u = user || {};
  const a = activity || {};
  const windowDays = a.window_days || 90;
  const signedSub = [
    fmtAgo(u.created_at),
    a.signup_platform && `via ${CHANNEL[a.signup_platform] || a.signup_platform}`,
  ].filter(Boolean).join(' · ');
  const seen = platformsSeen(a.platform_share_90d);
  const logins = u.login_count || 0;
  const sessions = a.sessions_30d || 0;
  const pushCount = a.push_device_count || 0;

  return (
    <section className="adm__card adm__card--flush" aria-labelledby="adm-activity-title">
      <div className="adm-card__head">
        <h2 className="adm-card__title" id="adm-activity-title">Account activity</h2>
        <span className="adm-card__meta">Signup, logins and the app they use</span>
      </div>
      <div className="adm-statgrid adm-statgrid--activity">
        <div className="adm-stat">
          <div className="adm-stat__label">Signed up</div>
          <div className={`adm-stat__value${u.created_at ? '' : ' adm-stat__value--empty'}`}>{fmtDate(u.created_at) || '—'}</div>
          {signedSub && <div className="adm-stat__sub">{signedSub}</div>}
        </div>
        <div className="adm-stat">
          <div className="adm-stat__label">Last login</div>
          {u.last_login_at
            ? <div className="adm-stat__value">{fmtDateTime(u.last_login_at)}</div>
            : <div className="adm-stat__value adm-stat__value--never">Never</div>}
          <div className="adm-stat__sub">
            {plural(logins, 'login')} total
            {u.last_active_at && ` · active ${fmtAgo(u.last_active_at)}`}
          </div>
        </div>
        <div className="adm-stat">
          <div className="adm-stat__label">App</div>
          {a.primary_platform
            ? <div className="adm-stat__value"><PlatformMark platform={a.primary_platform} /></div>
            : <div className="adm-stat__value adm-stat__value--empty">—</div>}
          <div className="adm-stat__sub">{appSub(a)}</div>
        </div>
        <div className="adm-stat">
          <div className="adm-stat__label">Last 30 days</div>
          <div className="adm-stat__value">{plural(sessions, 'session')}</div>
          <div className="adm-stat__sub">
            {a.push_enabled ? `Push enabled · ${plural(pushCount, 'device')}` : 'Push off'}
          </div>
        </div>
      </div>
      <div className="adm-activity__foot">
        <span><span className="adm-activity__k">Sign-in</span> · {PROVIDER[u.auth_provider] || '—'}</span>
        <span>
          <span className="adm-activity__k">Email</span> ·{' '}
          {u.email_verified
            ? <span className="adm-activity__ok">Verified</span>
            : <span className="adm-activity__warn">Not verified</span>}
        </span>
        <span>
          <span className="adm-activity__k">Platforms seen</span> · {seen || `None in ${windowDays} days`}
        </span>
      </div>
    </section>
  );
}
